import { CommonModule } from '@angular/common';
import { Component, ElementRef, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import { HttpService } from '../../services/http.service';
import { ConstantsService } from '../../constants/constants.service';
import { IdleService } from '../../services/idle.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ToastrModule, RouterLink],
  providers: [HttpService],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  user: any = {};
  formSubmitted: boolean = false;
  formSubmitLoader: boolean = false;
  loginForm!: FormGroup;
  forgotPasswordForm!: FormGroup;
  showForgotPassword: boolean = false;
  showPassword: boolean = false;
  showPrivacyPolicy: boolean = false;

  constructor(
    private httpService: HttpService,
    public constants: ConstantsService,
    private router: Router,
    private formBuilder: FormBuilder,
    private el: ElementRef,
    private toastr: ToastrService,
    private idleService: IdleService
  ) { }

  ngOnInit(): void {
    const storage = this.constants.getSession();
    if (storage) {
      this.router.navigate(['/dashboard']);
    }

    this.loginForm = this.formBuilder.group({
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(this.constants.VALIDATION_PATTERNS['EMAIL']),
        ],
      ],
      password: ['', Validators.required],
    });

    this.forgotPasswordForm = this.formBuilder.group({
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(this.constants.VALIDATION_PATTERNS['EMAIL']),
        ],
      ],
    });
  }

  login() {
  this.formSubmitted = true;
  if (this.loginForm.invalid) {
    return;
  }

  this.formSubmitLoader = true;
  const data = this.loginForm.value;

  this.httpService.postRequest(this.constants.SERVER_URLS['LOGIN'], data)
    .subscribe((res: any) => {
      this.formSubmitLoader = false;
      if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
        this.user = res.result;

        // Determine roles
        const roles = this.user.roles || [];
        this.user.isAdmin = roles.some((r: any) => r.keyRole === 'ADMIN');

        // 🔥 NEW LOGIC: Determine default role based on priority
        const defaultRole = this.determineDefaultRole(roles);

        this.user.role = defaultRole;

        // Save session
        this.constants.setRoleSession(defaultRole);
        this.constants.setSession(this.user);

        // Set role in backend & navigate
        this.setActiveRole(defaultRole.keyRole);
      } else {
        this.toastr.error(res.message,  );
      }
    }, () => (this.formSubmitLoader = false));
}

// 🔥 NEW METHOD: Determines the default role based on your requirements
determineDefaultRole(roles: any[]): any {
  if (!roles || roles.length === 0) {
    return null;
  }

  const roleKeys = roles.map((r: any) => r.keyRole);
  const hasHost = roleKeys.includes('HOST');
  const hasReception = roleKeys.includes('RECEPTION');
  const hasSecurity = roleKeys.includes('SECURITY');
  const hasAdmin = roleKeys.includes('ADMIN');

  // Priority logic based on your requirements
  // admin,host → HOST
  if (hasAdmin && hasHost && roleKeys.length === 2) {
    return roles.find((r: any) => r.keyRole === 'HOST');
  }

  // host,security → SECURITY
  if (hasHost && hasSecurity && !hasReception && !hasAdmin) {
    return roles.find((r: any) => r.keyRole === 'SECURITY');
  }

  // host,reception → RECEPTION
  if (hasHost && hasReception && !hasSecurity && !hasAdmin) {
    return roles.find((r: any) => r.keyRole === 'RECEPTION');
  }

  // admin,host,security,reception → HOST
  // admin,host,security → HOST
  // admin,host,reception → HOST
  if (hasAdmin && hasHost) {
    return roles.find((r: any) => r.keyRole === 'HOST');
  }

  // host,security,reception → RECEPTION
  if (hasHost && hasReception && hasSecurity && !hasAdmin) {
    return roles.find((r: any) => r.keyRole === 'RECEPTION');
  }

  // Fallback: If none of the above, prefer HOST > RECEPTION > SECURITY > ADMIN > first role
  if (hasHost) {
    return roles.find((r: any) => r.keyRole === 'HOST');
  }
  if (hasReception) {
    return roles.find((r: any) => r.keyRole === 'RECEPTION');
  }
  if (hasSecurity) {
    return roles.find((r: any) => r.keyRole === 'SECURITY');
  }
  if (hasAdmin) {
    return roles.find((r: any) => r.keyRole === 'ADMIN');
  }

  return roles[0]; // Ultimate fallback
}

setActiveRole(roleKey: string) {
  this.httpService
    .getRequest(this.constants.SERVER_URLS['SET_ACTIVE_ROLE'] + '/' + roleKey)
    .subscribe(
      (tokenRes: any) => {
        if (tokenRes.status === this.constants.RESPONSE_CODES['SUCCESS']) {
          this.user.sessionToken = tokenRes.result;
          this.constants.setSession(this.user);

          // 🔥 SIMPLIFIED: Navigate based on the selected role only
          const routeMap: { [key: string]: string } = {
            ADMIN: '/users',
            HOST: '/dashboard',
            RECEPTION: '/todays-visits',
            SECURITY: '/todays-visits',
          };

          const route = routeMap[roleKey] || '/settings';
          
          this.router.navigate([route]);
          this.toastr.success('Logged in successfully.');
          this.idleService.resetIdleWatch();
        } else {
          this.toastr.error('Failed to activate role',  );
        }
      },
      (err) => {
        console.error('Error activating role:', err);

        if (err.status === 403) {
          this.toastr.error('You no longer have access to this role.', 'Access Denied');
        } else {
          this.toastr.error('Something went wrong. Please try again.',  );
        }
      }
    );
}

  showForgotPasswordForm() {
    this.showForgotPassword = !this.showForgotPassword;
  }

  resetPassword() {
    if (this.forgotPasswordForm.invalid) {
      return;
    }

    const data = this.forgotPasswordForm.value;
    this.httpService
      .postRequest(this.constants.SERVER_URLS['RESET_PASSWORD'], data)
      .subscribe(
        (res: any) => {
          if (res.status === this.constants.RESPONSE_CODES['SUCCESS']) {
            this.toastr.success('Password reset link sent!');
            this.showForgotPassword = false;
          } else {
            this.toastr.error(res.message,  );
          }
        },
        (err) => {
          this.toastr.error(
            'An error occurred. Please try again later.',
             
          );
        }
      );
  }

  openPrivacyModal() {
    this.showPrivacyPolicy = true;
  }

  closePrivacyModal() {
    this.showPrivacyPolicy = false;
  }

}
